import { GWUtils } from './ca-utils';

/**
 * Get the current date and time in Eastern Time
 * @returns {object} An object with the following properties:
 * @returns {{date: Date, hour: number}} An object containing the date and hour
 */
export const getEasternDate = () => {
  const now = new Date();
  const options = {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour12: false,
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  // Format the current time to Eastern Time parts
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(now);

  // Reconstruct a Date object using the Eastern Time parts
  const year = parseInt(parts.find((part) => part.type === 'year').value, 10);
  const month = parseInt(parts.find((part) => part.type === 'month').value, 10) - 1;
  const day = parseInt(parts.find((part) => part.type === 'day').value, 10);
  const hour = parseInt(parts.find((part) => part.type === 'hour').value, 10);
  const minute = parseInt(parts.find((part) => part.type === 'minute').value, 10);
  const second = parseInt(parts.find((part) => part.type === 'second').value, 10);

  // Create a new Date object set to Eastern Time
  return {
    date: new Date(year, month, day, hour, minute, second),
    hour,
  };
};

export const calculateDateDifference = (date) => {
  const reviewDate = new Date(date);
  const now = new Date();
  const diff = now - reviewDate;
  const diffInSeconds = Math.floor(diff / 1000);
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInWeeks = Math.floor(diffInDays / 7);
  const diffInMonths = Math.floor(diffInDays / 30);
  const diffInYears = Math.floor(diffInDays / 365);

  if (diffInYears > 0) {
    return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
  } else if (diffInMonths > 0) {
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  } else if (diffInWeeks > 0) {
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  } else if (diffInDays > 0) {
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  } else if (diffInHours > 0) {
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else if (diffInMinutes > 0) {
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  } else {
    return `${diffInSeconds} second${diffInSeconds > 1 ? 's' : ''} ago`;
  }
};

/**
 * Format a date object to a string in the format 'dd-mm-yyyy' by default.
 * @param {object} options
 * @param {Date} [options.date] - The date object to format.
 * @param {string} [options.separator] - The separator to use between date parts.
 * @returns {string} The formatted date string.
 */
export const formatDate = ({ date, separator = '-' }) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return [month, day, year].join(separator);
};

/**
 * Get the name of the month for a given date.
 * @param {Date} date
 * @returns {string}
 */
export const getLongDate = (date) => {
  const month = date.toLocaleString('en-US', { month: 'long' });
  const day = date.getDate();
  const year = date.getFullYear();

  return `${month} ${day}, ${year}`;
};

/**
 * Get the name of the weekday for a given date.
 * @param {Date} date
 * @returns {string}
 */
export const getWeekdayName = (date) => {
  const optionsWeekday = { weekday: 'long' };
  const formatterWeekday = new Intl.DateTimeFormat('en-US', optionsWeekday);
  return formatterWeekday.format(date);
};

/**
 * Get the date in the format 'dd/mm'.
 * @param {Date} date
 * @returns {string}
 */
export const getMonthSlashDay = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return month + '/' + day;
};

/**
 * Get the list of dates from a comma-separated string.
 * @param {Date} date
 * @returns {string}
 */
export const CSVToArray = (datesString) => {
  if (!datesString) return [];
  return datesString.split(',').map((date) => date.trim());
};

/**
 * Check if a date is blocked.
 * @param {Date} date
 * @param {string[]} globalBlockOutDates
 * @param {string[]} productBlockOutDates
 * @param {string[]} productSoldOutDates
 * @param {string[]} repeatingDisabledDates
 * @returns {boolean}
 */
export const isBlockedDate = (date, globalBlockOutDates, productBlockOutDates, productSoldOutDates, repeatingDisabledDates, nextDeliveryDate) => {
  const formattedDate = formatDate({ date });
  const easternTodaysDate = getEasternDate().date;
  const easternCurrentHour = getEasternDate().hour;
  const easternCurrentDay = getEasternDate().date.getDay();
  const cutoffTime = getCutoffTime();
  const globalSoldoutDates = CSVToArray(window.Boarderie?.blockoutDates?.globalSoldoutDates);

  // Get the date for the next day
  const easternNextDate = getEasternDate().date;
  easternNextDate.setDate(easternNextDate.getDate() + 1);

  // Cutoff Validations
  let monthChanged = false;
  if (easternNextDate.getMonth() !== easternTodaysDate.getMonth()) {
    monthChanged = true;
  }

  let yearChanged = false;
  if (easternNextDate.getFullYear() !== easternTodaysDate.getFullYear()) {
    yearChanged = true;
  }

  const passedCutoffTime = easternCurrentHour >= cutoffTime;

  const validateCutoffTimeDate = date.getDate() === easternNextDate.getDate();

  let validateCutoffTimeMonth = date.getMonth() === easternTodaysDate.getMonth();
  if (monthChanged) {
    validateCutoffTimeMonth = date.getMonth() === easternNextDate.getMonth();
  }

  let validateCutoffTimeYear = date.getFullYear() === easternTodaysDate.getFullYear();
  if (yearChanged) {
    validateCutoffTimeYear = date.getFullYear() === easternNextDate.getFullYear();
  }

  const isAfterCuttoffTime = passedCutoffTime && validateCutoffTimeDate && validateCutoffTimeMonth && validateCutoffTimeYear
  const isBeforeToday = date <= easternTodaysDate;
  const nextDeliveryDateISO = nextDeliveryDate?.arrivalDateISO;

  const isSaturdaySpecialCase = [
    getWeekdayName(date) === 'Monday', // if the date is monday
    compareDates(date, new Date(nextDeliveryDateISO)), // is date is next available day,
    easternCurrentDay === 6, // if today is saturday
    date.getDate() - 2 === easternTodaysDate.getDate(), // if saturday is two days before the date(monday)
    easternCurrentHour > cutoffTime, // if current time is after saturday's cuttoff time
  ].every(Boolean)

  // if today is sunday, we need to block monday.
  const isSundaySpecialCase = [
    getWeekdayName(date) === 'Monday', // if the date is monday
    compareDates(date, new Date(nextDeliveryDateISO)), // is date is next available day,
    easternCurrentDay === 0, // if today is sunday
    date.getDate() - 1 === easternTodaysDate.getDate(), // if sunday is one day before the date(monday)
  ].every(Boolean)

  const isBlockedChecks = [
    { condition: 'global', result: globalBlockOutDates.includes(formattedDate) },
    { condition: 'product-blockout', result: productBlockOutDates.includes(formattedDate) },
    { condition: 'product-soldout', result: productSoldOutDates.includes(formattedDate) || globalSoldoutDates.includes(formattedDate) },
    { condition: 'repeating', result: repeatingDisabledDates.includes(getWeekdayName(date)) },
    { condition: 'beforeToday', result: isBeforeToday },
    {
      condition: 'afterCutoff',
      result: isAfterCuttoffTime,
    },
    {
      condition: "saturdaySpecialCase",
      result: isSaturdaySpecialCase,
    },
    {
      condition: "sundaySpecialCase",
      result: isSundaySpecialCase,
    },
  ];

  // Check if the date is always enabled
  const overrideBlockedDates = CSVToArray(window.Boarderie?.overrideBlockoutDates);
  if (
    overrideBlockedDates.length > 0 &&
    overrideBlockedDates.includes(formattedDate) &&
    !isAfterCuttoffTime &&
    !isBeforeToday &&
    !productBlockOutDates.includes(formattedDate) &&
    !productSoldOutDates.includes(formattedDate) &&
    !isSaturdaySpecialCase &&
    !isSundaySpecialCase
  ) {
    return {
      isBlocked: false,
      blockedConditions: [],
    };
  }

  const blockedConditions = isBlockedChecks.filter((check) => check.result).map((check) => check.condition);

  return {
    isBlocked: blockedConditions.length > 0,
    blockedConditions: blockedConditions,
  };
};

export function compareDates(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

/**
 * Format a date object to a string in the format 'yyyy-mm-dd'.
 * @param {Date} date - The date object to format.
 * @returns {string} The formatted date string.
 */
export const formatGiftCardDate = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();

  return [year, month, day].join('-');
};

/**
 * Check if the current time is within the preorder period
 * @param {Object} preorderConfig - The preorder configuration object
 * @returns {boolean}
 */
const isWithinPreorderPeriod = (preorderConfig) => {
  if (!preorderConfig) return false;

  const { startDate, startTime, endDate, endTime } = preorderConfig;
  if (!startDate || !startTime || !endDate || !endTime) return false;

  const easternNow = getEasternDate().date;

  // Parse start date and time
  const [startMonth, startDay, startYear] = startDate.split('-').map(Number);
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const startDateTime = new Date(startYear, startMonth - 1, startDay, startHour, startMinute, 0);

  // Parse end date and time
  const [endMonth, endDay, endYear] = endDate.split('-').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  const endDateTime = new Date(endYear, endMonth - 1, endDay, endHour, endMinute, 0);

  return easternNow >= startDateTime && easternNow <= endDateTime;
};

/**
 * Check if a delivery date is within the preorder delivery date range
 * @param {Date} date - The delivery date to check
 * @param {Object} preorderConfig - The preorder configuration object
 * @returns {boolean}
 */
const isWithinPreorderDeliveryRange = (date, preorderConfig) => {
  if (!preorderConfig) return false;

  const { preorderFirstDate, preorderLastDate } = preorderConfig;
  if (!preorderFirstDate || !preorderLastDate) return false;

  const formattedDate = formatDate({ date });

  // Parse first and last delivery dates
  const [firstMonth, firstDay, firstYear] = preorderFirstDate.split('-').map(Number);
  const firstDate = new Date(firstYear, firstMonth - 1, firstDay);

  const [lastMonth, lastDay, lastYear] = preorderLastDate.split('-').map(Number);
  const lastDate = new Date(lastYear, lastMonth - 1, lastDay);

  return date >= firstDate && date <= lastDate;
};

export const isPrivatePreorderDate = (date) => {
  const isPreorderSession = sessionStorage.getItem('preorder');
  const preorderConfig = window.Boarderie?.privatePreorder;
  const isPreorderEnabled = preorderConfig?.enabled;

  if (!isPreorderSession || !isPreorderEnabled) return false;

  // Check if current time is within preorder period
  if (!isWithinPreorderPeriod(preorderConfig)) return false;

  // Check if delivery date is within preorder delivery range
  return isWithinPreorderDeliveryRange(date, preorderConfig);
};

export const isGeneralPreorderDate = (date) => {
  const isPrivatePreorderSession = sessionStorage.getItem('preorder');
  const preorderConfig = window.Boarderie?.generalPreorder;
  const isPreorderEnabled = preorderConfig?.enabled;

  if (!isPreorderEnabled || isPrivatePreorderSession) return false;

  // Check if current time is within preorder period
  if (!isWithinPreorderPeriod(preorderConfig)) return false;

  // Check if delivery date is within preorder delivery range
  return isWithinPreorderDeliveryRange(date, preorderConfig);
};

export const availableToEditOrder = (blockedTime, cutoffTime, shipByDate) => {
  const nowInEST = getEasternDate();
  const shipByDateObj = GWUtils.toLocalDate(shipByDate);
  shipByDateObj.setHours(cutoffTime, 0, 0, 0);

  const hoursUntilShipByDateFromNowInEST = shipByDateObj.getTime() - nowInEST.date.getTime();
  return hoursUntilShipByDateFromNowInEST > blockedTime;
};


/**
 * Parse product-level preorder dates from window.Boarderie
 * @returns {{ startDate: Date, endDate: Date } | null}
 */
export const parseProductPreorderDates = (datesOverride) => {
  const dates = datesOverride || window.Boarderie?.blockoutDates?.product?.preorderDates;
  if (!dates || !Array.isArray(dates) || dates.length < 2) return null;

  const [startISO, endISO] = dates;
  const [sY, sM, sD] = startISO.split('-').map(Number);
  const [eY, eM, eD] = endISO.split('-').map(Number);

  const startDate = new Date(sY, sM - 1, sD);
  const endDate = new Date(eY, eM - 1, eD);

  // Check if preorder is still valid (end date >= today in Eastern)
  const { date: today } = getEasternDate();
  today.setHours(0, 0, 0, 0);
  if (endDate < today) return null;

  return { startDate, endDate };
};

export const isProductPreorderActive = () => {
  return parseProductPreorderDates() !== null;
};

/**
 * True if `date` is a premium (overnight-shipping) delivery day for dessert boards.
 * Combines two rules:
 *   1. Tuesday — unconditionally premium (logistics: no Sunday carrier pickup means
 *      Tuesday delivery always requires Monday overnight shipping).
 *   2. Additional days configured by the merchant via `window.Boarderie.premiumDelivery.days`.
 * Caller is responsible for gating on `boardType === 'dessert-board'`.
 * @param {Date} date
 * @returns {boolean}
 */
export const isPremiumDeliveryDayOfWeek = (date) => {
  if (!date) return false;
  if (date.getDay() === 2) return true;
  const raw = window.Boarderie?.premiumDelivery?.days;
  if (typeof raw !== 'string' || !raw) return false;
  const days = raw.split(',').map((d) => d.trim()).filter(Boolean);
  return days.includes(getWeekdayName(date));
};

export const getCutoffTime = () => {
  const { date: easternDate } = getEasternDate();
  const dayOfWeek = easternDate.getDay();

  switch (dayOfWeek) {
    case 0:
      return window.Boarderie.cutoffTimeDay7;
    case 1:
      return window.Boarderie.cutoffTimeDay1;
    case 2:
      return window.Boarderie.cutoffTimeDay2;
    case 3:
      return window.Boarderie.cutoffTimeDay3;
    case 4:
      return window.Boarderie.cutoffTimeDay4;
    case 5:
      return window.Boarderie.cutoffTimeDay5;
    case 6:
      return window.Boarderie.cutoffTimeDay6;
    default:
      return window.Boarderie.cutoffTimeDay1;
  }
};